/**
 * "More fields" section for any module form (customers, appointments, staff, services, products).
 *
 * Loads the business's custom fields for `module`, and when `recordId` is given (editing),
 * loads that record's saved values. The parent keeps the values and sends them with its
 * save request as `custom_fields`; the server stores them after saving the record.
 *
 *   const [customFields, setCustomFields] = useState({});
 *   <MoreFields module="staff" recordId={editId} values={customFields} onChange={setCustomFields} />
 *   ...save({ ...form, custom_fields: customFields })
 */

import React, { useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { MdExpandMore } from 'react-icons/md';
import { getData } from '../utils/serverHelper';
import { BUSINESS_FIELDS } from '../utils/urls';
import { prettyFieldName } from '../utils/customFieldModules';

const unwrap = (res) => {
  if (Array.isArray(res)) return res;
  if (res && Array.isArray(res.data)) return res.data;
  if (res && res.data && typeof res.data === 'object') return res.data;
  return res || [];
};

const inputType = (datatype) =>
  ({ FLOAT: 'number', INT: 'number', DATE: 'date', TIME: 'time' }[String(datatype || '').toUpperCase()] || 'text');

const MoreFields = ({ module, recordId, values = {}, onChange, defaultExpanded = false, title = 'More fields' }) => {
  const [fields, setFields] = useState([]);
  const [expanded, setExpanded] = useState(defaultExpanded);

  useEffect(() => {
    let alive = true;
    getData(`${BUSINESS_FIELDS}?module=${encodeURIComponent(module)}`)
      .then((res) => {
        if (alive) setFields(Array.isArray(unwrap(res)) ? unwrap(res) : []);
      })
      .catch(() => alive && setFields([]));
    return () => {
      alive = false;
    };
  }, [module]);

  useEffect(() => {
    let alive = true;
    if (!recordId) return undefined;
    getData(`${BUSINESS_FIELDS}/values/${encodeURIComponent(module)}/${recordId}`)
      .then((res) => {
        const saved = unwrap(res);
        if (alive && saved && typeof saved === 'object' && !Array.isArray(saved)) {
          onChange?.({ ...saved });
          if (Object.keys(saved).length) setExpanded(true);
        }
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [module, recordId]);

  if (!fields.length) return null;

  const setValue = (fieldId, value) => onChange?.({ ...values, [fieldId]: value });
  const requiredMissing = fields.some((f) => f.is_required && !String(values[f.field_id] ?? '').trim());

  return (
    <Accordion
      expanded={expanded || requiredMissing}
      onChange={(_, open) => setExpanded(open)}
      disableGutters
      sx={{ boxShadow: 'none', border: '1px solid', borderColor: 'divider', borderRadius: 2, '&:before': { display: 'none' }, mt: 2 }}
    >
      <AccordionSummary expandIcon={<MdExpandMore />}>
        <Typography sx={{ fontWeight: 600 }}>
          {title} <Typography component="span" color="text.secondary">({fields.length})</Typography>
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
          {fields.map((f) => {
            const label = prettyFieldName(f.label);
            const value = values[f.field_id] ?? '';
            const options = String(f.values || '')
              .split(',')
              .map((o) => o.trim())
              .filter(Boolean);
            if (options.length) {
              return (
                <FormControl key={f.field_id} size="small" fullWidth required={!!f.is_required}>
                  <InputLabel>{label}</InputLabel>
                  <Select label={label} value={value} onChange={(e) => setValue(f.field_id, e.target.value)}>
                    {!f.is_required && <MenuItem value="">—</MenuItem>}
                    {options.map((o) => (
                      <MenuItem key={o} value={o}>
                        {o}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              );
            }
            const type = inputType(f.datatype);
            return (
              <TextField
                key={f.field_id}
                label={label}
                type={type}
                size="small"
                fullWidth
                required={!!f.is_required}
                value={value}
                onChange={(e) => setValue(f.field_id, e.target.value)}
                InputLabelProps={type === 'date' || type === 'time' ? { shrink: true } : undefined}
                inputProps={type === 'number' ? { step: 'any' } : undefined}
              />
            );
          })}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default MoreFields;
